import React, { useMemo, useState, useEffect } from 'react';
import { formatDistance } from 'date-fns';
import { Typography } from '@mui/material';
import { getLocalDate } from '@surfline/web-common';

import { MDY } from 'common/constants';
import { postedDate } from 'utils/formatReportDate';

import styles from './LastUpdate.module.scss';

export interface LastUpdateProps {
  abbrTimezone?: string;
  dateFormat?: string;
  isExpired?: boolean;
  rawTimestampLabel?: boolean;
  timestamp: number | null;
  utcOffset?: number;
}

export const LastUpdate: React.FC<LastUpdateProps> = ({
  abbrTimezone = 'PST',
  dateFormat = MDY,
  isExpired = false,
  timestamp = null,
  rawTimestampLabel = false,
  utcOffset = -8,
}) => {
  const [showDate, setShowDate] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const timestampText = useMemo(() => {
    if (!timestamp) return null;

    if (showDate) {
      return postedDate(timestamp, utcOffset, abbrTimezone, true, dateFormat);
    }

    return formatDistance(
      getLocalDate(timestamp, utcOffset),
      getLocalDate(Date.now() / 1000, utcOffset),
      {
        includeSeconds: true,
        addSuffix: true,
      },
    );
  }, [abbrTimezone, dateFormat, showDate, timestamp, utcOffset]);

  if (!timestamp || !hasMounted) return null;

  return (
    <button
      className={styles.button}
      data-testid="last-update"
      onClick={() => setShowDate(!showDate)}
      type="button"
    >
      <Typography
        variant={rawTimestampLabel ? 'caption1' : 'footnote'}
        color={!isExpired ? 'text.secondary' : 'error'}
      >
        {rawTimestampLabel ? timestampText : `Last updated ${timestampText}`}
      </Typography>
    </button>
  );
};

export default LastUpdate;
